/*
 * CTA-sektion med emotionell tonalitet och säsongsbaserad logik
 * Marknadsplan: Brand Story-tonalitet - poetisk, varm, emotionell men professionell
 * Nyckelord: dröm, längtan, omsorg, hantverk, trygghet
 *
 * Säsongslogik: Om title/subtitle/buttonText inte anges explicit,
 * används säsongsanpassade texter baserat på aktuell årstid.
 * Explicit angivna props har alltid företräde.
 */
import { Button } from "@/components/ui/button";
import { Phone, Calendar } from "lucide-react";
import { FadeUp } from "@/components/ScrollAnimations";
import { useOffert } from "@/components/OffertModal";
import { getSeasonalCtaForContext } from "@/lib/seasonalCta";
import type { SeasonalCta } from "@/lib/seasonalCta";
import { useMemo } from "react";

interface CtaSectionProps {
  title?: string;
  subtitle?: string;
  buttonText?: string;
  variant?: "default" | "dark" | "light";
  /** Kontexttyp för säsongsanpassning (default: "general") */
  seasonalContext?: "hero" | "price" | "process" | "general";
  /** Visa säsongsbaserad urgency-text under knapparna */
  showUrgency?: boolean;
}

export default function CtaSection({
  title,
  subtitle,
  buttonText,
  variant = "default",
  seasonalContext = "general",
  showUrgency = false,
}: CtaSectionProps) {
  const { openOffert } = useOffert();
  const isDark = variant === "dark";

  const seasonal: SeasonalCta = useMemo(
    () => getSeasonalCtaForContext(seasonalContext),
    [seasonalContext]
  );

  // Explicit props har företräde, annars säsongsbaserade texter
  const resolvedTitle = title ?? seasonal.title;
  const resolvedSubtitle = subtitle ?? seasonal.subtitle;
  const resolvedButtonText = buttonText ?? seasonal.buttonText;

  return (
    <section className={isDark ? "bg-navy text-white py-12 sm:py-16 lg:py-20" : "bg-sand py-12 sm:py-16 lg:py-20"}>
      <div className="container max-w-3xl text-center">
        <FadeUp>
          <h2 className={`font-heading text-2xl sm:text-3xl lg:text-4xl font-bold mb-3 sm:mb-4 ${isDark ? "text-white" : "text-navy"}`}>
            {resolvedTitle}
          </h2>
          <p className={`text-base sm:text-lg mb-6 sm:mb-8 leading-relaxed ${isDark ? "text-white/80" : "text-muted-foreground"}`}>
            {resolvedSubtitle}
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4">
            <Button
              size="lg"
              onClick={() => openOffert("cta-section")}
              className="bg-rose hover:bg-rose-dark text-white font-medium w-full sm:w-auto px-8 py-6 text-base cta-pulse"
            >
              {resolvedButtonText}
            </Button>
            <a
              href="tel:+46841026111"
              className={`flex items-center justify-center gap-2 text-base font-medium py-3 ${isDark ? "text-white/80 hover:text-white" : "text-muted-foreground hover:text-navy"} transition-colors`}
            >
              <Phone className="w-5 h-5" />
              Ring oss: 08-410 26 111
            </a>
          </div>
          {showUrgency && seasonal.urgencyText && (
            <p className={`flex items-center justify-center gap-1.5 text-xs sm:text-sm mt-3 sm:mt-4 font-medium ${isDark ? "text-copper-light" : "text-copper"}`}>
              <Calendar className="w-3.5 h-3.5" />
              {seasonal.urgencyText}
            </p>
          )}
          <p className={`text-xs sm:text-sm mt-3 sm:mt-4 ${isDark ? "text-white/70" : "text-muted-foreground/70"}`}>
            Hembesöket är kostnadsfritt och helt kravlöst.
          </p>
        </FadeUp>
      </div>
    </section>
  );
}
